import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  notification,
  Popconfirm,
  Rate,
  Typography,
} from "antd";
import { useParams } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import {
  IBettingWebsite,
  IBettingWebsiteContacts,
} from "../../../types/BettingWebsite";
import { editBettingWebsiteContacts, getBettingWebsiteByID } from "../action";

const { Text } = Typography;

type Props = {
  fetchData: () => void;
};

const labelMap: Record<keyof IBettingWebsiteContacts, string> = {
  Email: "Email",
  Web: "Website",
  Facebook: "Facebook",
  Telegram: "Telegram",
  Instagram: "Instagram",
  X: "X (Twitter)",
};

const EditContacts: React.FC<Props> = ({ fetchData }) => {
  const { id } = useParams();
  const [form] = Form.useForm<IBettingWebsiteContacts>();
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);

  const fetchContacts = async () => {
    setIsLoading(true);
    try {
      const contact: IBettingWebsite | null = await getBettingWebsiteByID(id!);
      if (contact) form.setFieldsValue(contact.Contacts);
    } catch (err: any) {
      api.error({
        message: "Failed to fetch contacts",
        description: err?.message || "Unexpected error occurred",
      });
    }
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);
      const success = await editBettingWebsiteContacts(id!, values);

      if (success) {
        fetchData();
        api.success({ message: "Contacts updated successfully" });
      }
    } catch (err: any) {
      api.error({
        message: "Failed to update contacts",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchContacts();
  }, [id]);

  return (
    <Form form={form} layout="vertical" className="space-y-4">
      {contextHolder}
      <div className=" grid grid-cols-3 gap-4">
        {(Object.keys(labelMap) as (keyof IBettingWebsiteContacts)[]).map(
          (key) => (
            <Form.Item key={key} label={labelMap[key]} name={key}>
              <Input placeholder={labelMap[key]} />
            </Form.Item>
          )
        )}
      </div>
      <Form.Item shouldUpdate>
        {() => (
          <Popconfirm
            title="Are you sure you want to update contacts?"
            onConfirm={handleSubmit}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<FaEdit />}
              loading={isLoading}
              type="primary"
              className="w-full"
              disabled={
                !form.isFieldsTouched(true) ||
                form.getFieldsError().some((f) => f.errors.length > 0)
              }
            >
              {isLoading ? "Updating Contacts..." : "Edit Contacts"}
            </Button>
          </Popconfirm>
        )}
      </Form.Item>
    </Form>
  );
};

export default EditContacts;
